<template>
  <div class="bn-wrap">

    <!-- Texto de la pregunta -->
    <div class="bn-row">
      <label class="bn-label">Texto de la pregunta</label>
      <input
        class="bn-input"
        :class="{ 'bn-input--error': question.error }"
        type="text"
        placeholder="Escribe la pregunta aquí..."
        v-model="question.texto"
      />
      <p v-if="question.error" class="bn-error-msg">Ingrese la pregunta.</p>
    </div>
    
    <div class="bn-row">
      <label class="bn-label">Descripción <span>(opcional)</span></label>
      <textarea class="bn-input" type="text" placeholder="Describe lo que se espera en la respuesta..." v-model="question.descripcion" ></textarea>
    </div>

    <!-- Nota -->
    <div class="bn-row">
      <label class="bn-label">Nota <span>(opcional)</span></label>
      <input class="bn-input" type="text" placeholder="Escribe una nota corta o definición..." v-model="question.nota_para_registrante" />
    </div>

    <!-- Elementos (Conceptos) a evaluar -->
    <div class="bn-row">
      <div class="bn-subfield-header">
        <label class="bn-label mt-0">Conceptos a evaluar</label>
        <button type="button" class="bn-add-btn" @click="addConcepto">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          Agregar
        </button>

        <!-- Obligatoria -->
        <label class="bn-check-row ml-auto">
          <input type="checkbox" v-model="question.obligatoria" />
          <span>Obligatoria</span>
        </label>
      </div>
      <p v-if="question.conceptoError" class="bn-error-msg">Deben llenarse todos los conceptos.</p>
      <p v-if="question.conceptoMinError" class="bn-error-msg">Minimo 1 concepto.</p>

      <div class="bn-options-list mt-2">
        <div v-for="(concepto, i) in question.conceptos" :key="'c_'+i" class="bn-option-row">
          <span class="bn-option-prefix">{{ i + 1 }}.</span>
          <input
            class="bn-input"
            :class="{ 'bn-input--error': concepto === '' && question.conceptoError }"
            type="text"
            :placeholder="'Concepto ' + (i + 1)"
            v-model="question.conceptos[i]"
          />
          <button v-if="question.conceptos.length > 1" type="button" class="bn-remove-btn" @click="removeConcepto(i)">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>
      </div>

      <label class="bn-check-row ml-6 mt-1 mb-3">
        <input type="checkbox" v-model="question.otro" />
        <span>Incluir opcion "Otro"</span>
      </label>
      <div v-if="question.otro" class="bn-row ml-6">
        <label class="q-label">Tipo de dato del campo "Otro"</label>
        <select class="q-input q-input--select" v-model="question.tipo_otro">
          <option value="texto">Texto</option>
          <option value="numero">Número</option>
          <option value="fecha">Fecha</option>
          <option value="hora">Hora</option>
        </select>
      </div>
      
    </div>

    <!-- Nombres de las dos opciones -->
    <div class="bn-row ml-6">
      <label class="bn-label">Opciones para cada concepto</label>

      <div class="flex gap-5">
        <div class="bn-row flex-1">
          <label class="bn-label">
            <span class="bn-pill bn-pill--pos">A</span> 
            Muestra sub-formulario
          </label>
          <input class="bn-input" type="text" placeholder="Ej: Sí" v-model="question.opcion_si" />
        </div>
        <div class="bn-row flex-1">
          <label class="bn-label">
            <span class="bn-pill bn-pill--neg">B</span>
            No muestra nada
          </label>
          <input class="bn-input" type="text" placeholder="Ej: No" v-model="question.opcion_no" />
        </div>

      </div>
    </div>

    <!-- Separador -->
    <div class="bn-divider ml-6">
      <span>Sub-formulario al seleccionar <strong>{{ question.opcion_si || 'opcion A' }}</strong></span>
    </div>

    <!-- Tipo de sub-campo -->
    <div class="bn-row ml-6">
      <label class="bn-label">Tipo de respuesta adicional</label>
      <div class="bn-type-grid">
        <button
          v-for="opt in subTypes"
          :key="opt.value"
          type="button"
          class="bn-type-btn"
          :class="{ 'bn-type-btn--active': question.sub_tipo === opt.value }"
          @click="selectSubType(opt.value)"
        >
          <span class="bn-type-icon" v-html="opt.icon"></span>
          <span class="bn-type-label">{{ opt.label }}</span>
        </button>
      </div>
      <p v-if="question.subTipoError" class="bn-error-msg">Selecciona un tipo de respuesta.</p>
    </div>

    <!-- Radio buttons -->
    <transition name="bn-fade">
      <div v-if="question.sub_tipo === 'radio'" class="bn-subfield-box ml-6">
        <div class="bn-row" style="margin-top:8px">
          <label class="bn-label">Etiqueta del campo <span style="font-weight:400">(opcional)</span></label>
          <input class="bn-input" type="text" placeholder="Ej. escríbe la subpregunta" v-model="question.sub_label" />
        </div>
        <div class="bn-subfield-header">
          <span class="bn-label" style="margin:0">Opciones de radio</span>
          <button type="button" class="bn-add-btn" @click="addSubOption">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            Agregar
          </button>
        </div>
        <p v-if="question.subOptionError" class="bn-error-msg">Deben llenarse todas las opciones.</p>
        <p v-if="question.subMinError" class="bn-error-msg">Minimo 2 opciones.</p>
        <div class="bn-options-list">
          <div v-for="(opt, i) in question.sub_opciones" :key="i" class="bn-option-row">
            <span class="bn-option-prefix">{{ i + 1 }}.</span>
            <input
              class="bn-input"
              :class="{ 'bn-input--error': opt === '' && question.subOptionError }"
              type="text"
              :placeholder="'Opcion ' + (i + 1)"
              v-model="question.sub_opciones[i]"
            />
            <button v-if="question.sub_opciones.length > 2" type="button" class="bn-remove-btn" @click="removeSubOption(i)">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            </button>
          </div>
        </div>
        <div class="bn-row" style="margin-top:8px">
          <label class="bn-label">Disposicion</label>
          <div style="display:flex;gap:12px">
            <label class="bn-check-row">
              <input type="radio" v-model="question.sub_disposicion" value="horizontal" />
              <span>Horizontal</span>
            </label>
            <label class="bn-check-row">
              <input type="radio" v-model="question.sub_disposicion" value="vertical" />
              <span>Vertical</span>
            </label>
          </div>
        </div>
      </div>
    </transition>

    <!-- Checkboxes -->
    <transition name="bn-fade">
      <div v-if="question.sub_tipo === 'checkbox'" class="bn-subfield-box ml-6">
        <div class="bn-row" style="margin-top:8px">
          <label class="bn-label">Etiqueta del campo <span style="font-weight:400">(opcional)</span></label>
          <input class="bn-input" type="text" placeholder="Ej. escríbe la subpregunta" v-model="question.sub_label" />
        </div>
        <div class="bn-subfield-header">
          <span class="bn-label" style="margin:0">Opciones de checkbox</span>
          <button type="button" class="bn-add-btn" @click="addSubOption">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            Agregar
          </button>
        </div>
        <p v-if="question.subOptionError" class="bn-error-msg">Deben llenarse todas las opciones.</p>
        <p v-if="question.subMinError" class="bn-error-msg">Minimo 2 opciones.</p>
        <div class="bn-options-list">
          <div v-for="(opt, i) in question.sub_opciones" :key="i" class="bn-option-row">
            <span class="bn-option-prefix">{{ i + 1 }}.</span>
            <input
              class="bn-input"
              :class="{ 'bn-input--error': opt === '' && question.subOptionError }"
              type="text"
              :placeholder="'Opcion ' + (i + 1)"
              v-model="question.sub_opciones[i]"
            />
            <button v-if="question.sub_opciones.length > 2" type="button" class="bn-remove-btn" @click="removeSubOption(i)">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
            </button>
          </div>
        </div>
        <div class="bn-row" style="margin-top:8px">
          <label class="bn-label">Disposicion</label>
          <div style="display:flex;gap:12px">
            <label class="bn-check-row">
              <input type="radio" v-model="question.sub_disposicion" value="horizontal" />
              <span>Horizontal</span>
            </label>
            <label class="bn-check-row">
              <input type="radio" v-model="question.sub_disposicion" value="vertical" />
              <span>Vertical</span>
            </label>
          </div>
        </div>
      </div>
    </transition>

    <!-- Input (texto / numero / fecha / hora) -->
    <transition name="bn-fade">
      <div v-if="question.sub_tipo === 'input'" class="bn-subfield-box ml-6">
        <div class="bn-row" style="margin-top:8px">
          <label class="bn-label">Etiqueta del campo <span style="font-weight:400">(opcional)</span></label>
          <input class="bn-input" type="text" placeholder="Ej. escríbe la subpregunta" v-model="question.sub_label" />
        </div>
        <div class="bn-row">
          <label class="bn-label">Tipo de dato</label>
          <select class="bn-input bn-input--select" v-model="question.sub_input_tipo">
            <option value="texto">Texto</option>
            <option value="numero">Numero</option>
            <option value="fecha">Fecha</option>
            <option value="hora">Hora</option>
          </select>
        </div>
        <transition name="bn-fade">
          <div v-if="question.sub_input_tipo === 'numero'" class="bn-range-row">
            <div class="bn-range-field">
              <label class="bn-label">Minimo <span style="font-weight:400">(opcional)</span></label>
              <input class="bn-input" type="number" v-model.number="question.sub_rango_min" placeholder="Sin limite" />
            </div>
            <div class="bn-range-field">
              <label class="bn-label">Maximo <span style="font-weight:400">(opcional)</span></label>
              <input class="bn-input" type="number" v-model.number="question.sub_rango_max" placeholder="Sin limite" />
            </div>
          </div>
        </transition>
        
      </div>
    </transition>

  </div>
</template>

<script>
export default {
  name: "binary-question" /* tipo: OPCION_SEGUIMIENTO */,
  props: {
    question: { required: true, type: Object },
  },
  data() {
    return {
      subTypes: [
        {
          value: "radio",
          label: "Radio",
          icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/></svg>',
        },
        {
          value: "checkbox",
          label: "Checkbox",
          icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="m10.933 13.519-2.226-2.226-1.414 1.414 3.774 3.774 5.702-6.84-1.538-1.282z"/><path d="M19 3H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2h14c1.103 0 2-.897 2-2V5c0-1.103-.897-2-2-2zM5 19V5h14l.002 14H5z"/></svg>',
        },
        {
          value: "input",
          label: "Campo",
          icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M4 9h16v2H4zm0 4h10v2H4z"/></svg>',
        },
      ],
    };
  },
  mounted() {
    if (!this.question.conceptos)  this.$set(this.question, "conceptos",  ["", ""]);
    if (this.question.conceptoError === undefined) this.$set(this.question, "conceptoError", false);
    if (this.question.conceptoMinError === undefined) this.$set(this.question, "conceptoMinError", false);
    
    if (!this.question.opcion_si)  this.$set(this.question, "opcion_si",  "Si");
    if (!this.question.opcion_no)  this.$set(this.question, "opcion_no",  "No");
    if (this.question.sub_tipo === undefined)       this.$set(this.question, "sub_tipo",       null);
    if (!this.question.sub_opciones)               this.$set(this.question, "sub_opciones",   ["", ""]);
    if (!this.question.sub_disposicion)            this.$set(this.question, "sub_disposicion","horizontal");
    if (!this.question.sub_input_tipo)             this.$set(this.question, "sub_input_tipo", "texto");
    if (this.question.sub_rango_min === undefined)  this.$set(this.question, "sub_rango_min",  null);
    if (this.question.sub_rango_max === undefined)  this.$set(this.question, "sub_rango_max",  null);
    if (!this.question.sub_label)                  this.$set(this.question, "sub_label",      "");
    if (this.question.subTipoError === undefined)   this.$set(this.question, "subTipoError",   false);
    if (this.question.subOptionError === undefined) this.$set(this.question, "subOptionError", false);
    if (this.question.subMinError === undefined)    this.$set(this.question, "subMinError",    false);
  },
  methods: {
    selectSubType(val) {
      this.question.sub_tipo = val;
      this.question.subTipoError = false;
      if ((val === "radio" || val === "checkbox") && (!this.question.sub_opciones || this.question.sub_opciones.length < 2)) {
        this.$set(this.question, "sub_opciones", ["", ""]);
      }
    },
    addSubOption() {
      this.question.sub_opciones.push("");
      this.question.subMinError = false;
    },
    removeSubOption(index) {
      if (this.question.sub_opciones.length > 2) {
        this.question.sub_opciones.splice(index, 1);
        this.question.subOptionError = false;
      } else {
        this.question.subMinError = true;
      }
    },
    addConcepto() {
      this.question.conceptos.push("");
      this.question.conceptoMinError = false;
    },
    removeConcepto(index) {
      if (this.question.conceptos.length > 1) {
        this.question.conceptos.splice(index, 1);
        this.question.conceptoError = false;
      } else {
        this.question.conceptoMinError = true;
      }
    },
    validateQuestion() {
      let valid = true;
      if (!this.question.texto || this.question.texto.trim() === "") {
        this.question.error = true; valid = false;
      } else { this.question.error = false; }

      if (!this.question.conceptos || this.question.conceptos.length < 1) {
        this.question.conceptoMinError = true; valid = false;
      } else { this.question.conceptoMinError = false; }

      if (this.question.conceptos && this.question.conceptos.some(c => c.trim() === "")) {
        this.question.conceptoError = true; valid = false;
      } else { this.question.conceptoError = false; }

      if (!this.question.sub_tipo) {
        this.question.subTipoError = true; valid = false;
      } else { this.question.subTipoError = false; }
      if (this.question.sub_tipo === "radio" || this.question.sub_tipo === "checkbox") {
        if (this.question.sub_opciones.length < 2) {
          this.question.subMinError = true; valid = false;
        } else { this.question.subMinError = false; }
        if (this.question.sub_opciones.some(o => o.trim() === "")) {
          this.question.subOptionError = true; valid = false;
        } else { this.question.subOptionError = false; }
      }
      return valid;
    },
    remove() { this.$emit("click"); },
  },
};
</script>

<style scoped>
.bn-wrap { display: flex; flex-direction: column; gap: 14px; font-size: 0.82rem; }
.bn-row { display: flex; flex-direction: column; gap: 4px; }
.bn-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #64748b; }
.bn-label span {font-weight:400; text-transform:none;}
.bn-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e1; border-radius: 7px; background: #f8fafc; color: #334155; font-size: 0.82rem; transition: border-color 0.15s, box-shadow 0.15s; outline: none; box-sizing: border-box; }
.bn-input:focus { border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.12); background: #fff; }
.bn-input--error { border-color: #f87171 !important; }
.bn-input--select { width: auto; min-width: 140px; max-width: 220px; }
.bn-error-msg { font-size: 0.7rem; color: #ef4444; margin: 0; }
.bn-check-row { display: flex; align-items: center; gap: 6px; cursor: pointer; }
.bn-check-row input[type=checkbox] { width: 14px; height: 14px; accent-color: #10b981; cursor: pointer; }
.bn-check-row input[type=radio] { width: 14px; height: 14px; accent-color: #10b981; cursor: pointer; }
.bn-check-row span { font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #475569; }
.bn-options-names { display: flex; flex-direction: column; gap: 8px; }
.bn-name-field { display: flex; align-items: center; gap: 8px; }
.bn-name-field .bn-input { flex: 1; }
.bn-pill { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; font-size: 0.7rem; font-weight: 800; flex-shrink: 0; }
.bn-pill--pos { background: #d1fae5; color: #065f46; border: 1.5px solid #34d399; }
.bn-pill--neg { background: #fee2e2; color: #7f1d1d; border: 1.5px solid #f87171; }
.bn-hint { font-size: 0.65rem; color: #94a3b8; flex-shrink: 0; white-space: nowrap; }
.bn-divider { display: flex; align-items: center; gap: 8px; font-size: 0.72rem; color: #10b981; font-weight: 600; border-top: 1px dashed #a7f3d0; padding-top: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
.bn-divider strong { font-weight: 800; }
.bn-type-grid { display: flex; gap: 8px; flex-wrap: wrap; }
.bn-type-btn { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 10px 16px; border: 2px solid #e2e8f0; border-radius: 10px; background: #f8fafc; color: #64748b; font-size: 0.7rem; font-weight: 600; text-transform: uppercase; cursor: pointer; transition: all 0.18s; min-width: 72px; }
.bn-type-btn:hover { border-color: #10b981; color: #10b981; background: #f0fdf4; }
.bn-type-btn--active { border-color: #10b981; color: #10b981; background: #d1fae5; box-shadow: 0 0 0 3px rgba(16,185,129,0.15); }
.bn-type-icon { width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; }
.bn-type-icon svg { width: 18px; height: 18px; }
.bn-type-label { line-height: 1; }
.bn-subfield-box { background: #f0fdf4; border: 1px solid #a7f3d0; border-radius: 10px; padding: 14px; display: flex; flex-direction: column; gap: 10px; }
.bn-subfield-header { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.bn-add-btn { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border: 1px solid #a7f3d0; border-radius: 6px; background: #d1fae5; color: #065f46; font-size: 0.72rem; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.bn-add-btn:hover { background: #a7f3d0; }
.bn-add-btn svg { width: 12px; height: 12px; }
.bn-options-list { display: flex; flex-direction: column; gap: 5px; }
.bn-option-row { display: flex; align-items: center; gap: 6px; }
.bn-option-prefix { font-size: 0.72rem; color: #94a3b8; min-width: 18px; text-align: right; flex-shrink: 0; }
.bn-remove-btn { display: flex; align-items: center; justify-content: center; width: 22px; height: 22px; border: 1px solid #fca5a5; border-radius: 5px; background: #fef2f2; color: #ef4444; cursor: pointer; flex-shrink: 0; transition: all 0.15s; }
.bn-remove-btn:hover { background: #fee2e2; }
.bn-remove-btn svg { width: 12px; height: 12px; }
.bn-range-row { display: flex; gap: 12px; flex-wrap: wrap; }
.bn-range-field { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 100px; }
.bn-fade-enter-active, .bn-fade-leave-active { transition: opacity 0.2s, transform 0.2s; }
.bn-fade-enter, .bn-fade-leave-to { opacity: 0; transform: translateY(-6px); }

.q-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #64748b; }
.q-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e1; border-radius: 7px; background: #f8fafc; color: #334155; font-size: 0.82rem; transition: border-color 0.15s, box-shadow 0.15s; outline: none; box-sizing: border-box; }
.q-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.12); background: #fff; }
.q-input--select { width: auto; min-width: 120px; max-width: 200px; }
</style>

